/**
 * @file    baseSerializer.ts
 * @scope   SHARED
 * @summary Engine-owned serialization core (Template Method): collection identity, originId
 *          resolution and edit-mode lookup; the subtype delta fills each collection body.
 * @consumes SerializerDelta hooks, CustomConfig
 * @produces serializeWithDelta, deserializeWithDelta, SerializeOptions
 * @sideEffects `serializeWithDelta` assigns `config.queryCollections` and returns the same object
 *              (historic behaviour of `extractEnterpriseDataToWidget`, kept on purpose).
 * @notes   Subtype-agnostic: knows nothing about Fields, filters or calc types (§7.2).
 */
import { CustomConfig } from 'models/WidgetModel';
import type { SerializerDelta, StoredCollection } from '../contracts';

export type SerializeOptions = {
  trend?: unknown;
  /** Guardrails collections, used to resolve a missing `originId`. */
  availableCollections?: { value?: string; originId?: string | null }[];
};

/**
 * Writes the subtype slice into `config.queryCollections`.
 *
 * @param config Widget config to update; returned untouched when undefined.
 * @param slice Subtype state.
 * @param delta Subtype hooks that list the collections and build each body.
 * @param options Trend + Guardrails collections.
 * @returns The same config reference.
 */
export const serializeWithDelta = <TSlice>(
  config: CustomConfig | undefined,
  slice: TSlice,
  delta: Pick<
    SerializerDelta<TSlice>,
    'getCollections' | 'serializeCollection' | 'serializeWidget'
  >,
  options: SerializeOptions = {}
): CustomConfig | undefined => {
  if (!config) return config;

  // WHY: widget-level keys are written even with no collections (CPR validation needs them).
  if (delta.serializeWidget) Object.assign(config, delta.serializeWidget(slice));

  const refs = delta.getCollections(slice);
  // WHY: nothing selected → keep what was persisted (historic behaviour).
  if (refs.length === 0) return config;

  const persisted = (config.queryCollections ?? []) as StoredCollection[];

  config.queryCollections = refs.map((ref) => {
    const originalCollection = persisted.find((qc) => qc.dataCollectionGuid === ref.guid);
    const originId =
      ref.originId ||
      options.availableCollections?.find((c) => c.value === ref.guid)?.originId ||
      null;

    return {
      dataCollectionGuid: ref.guid || null,
      originId: originId,
      ...delta.serializeCollection(slice, ref, { originalCollection, trend: options.trend }),
    } as any;
  });

  return config;
};

/**
 * Rebuilds the subtype slice from `config.queryCollections`.
 *
 * @param config Persisted widget config.
 * @param delta Subtype hooks that rebuild the slice / provide the empty slice.
 * @returns The subtype slice; `emptySlice()` when nothing is persisted.
 */
export const deserializeWithDelta = <TSlice>(
  config: CustomConfig | undefined,
  delta: Pick<
    SerializerDelta<TSlice>,
    'deserializeCollections' | 'emptySlice' | 'deserializeWidget'
  >
): TSlice => {
  const collections = (config?.queryCollections ?? []) as StoredCollection[];
  const slice =
    collections.length === 0
      ? delta.emptySlice()
      : delta.deserializeCollections(collections.filter((qc) => !!qc.dataCollectionGuid));

  return delta.deserializeWidget
    ? delta.deserializeWidget(config as Record<string, any> | undefined, slice)
    : slice;
};
